import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, ClipboardList, Search, Handshake, Calendar as CalendarIcon, DollarSign } from 'lucide-react';
const features = [{
  icon: <ClipboardList className="w-5 h-5" data-id="element-6396" />,
  title: 'Program smarter, not from memory',
  description: "Define what kind of act you need, what you can spend, and what room you're trying to fill. Booker does the matching.",
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6397">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6398">
          Event Need
        </div>
        <div className="space-y-3" data-id="element-6399">
          <div className="grid grid-cols-2 gap-3" data-id="element-6400">
            <div data-id="element-6401">
              <div className="text-xs text-textMuted mb-1" data-id="element-6402">Date</div>
              <div className="px-3 py-2 bg-surface border border-border rounded text-sm" data-id="element-6403">
                Jun 18, 2026
              </div>
            </div>
            <div data-id="element-6404">
              <div className="text-xs text-textMuted mb-1" data-id="element-6405">Room</div>
              <div className="px-3 py-2 bg-surface border border-border rounded text-sm" data-id="element-6406">
                Main Stage
              </div>
            </div>
          </div>
          <div data-id="element-6407">
            <div className="text-xs text-textMuted mb-1" data-id="element-6408">Genre</div>
            <div className="px-3 py-2 bg-surface border border-border rounded text-sm" data-id="element-6409">
              Folk / Americana
            </div>
          </div>
          <div data-id="element-6410">
            <div className="text-xs text-textMuted mb-1" data-id="element-6411">Budget</div>
            <div className="px-3 py-2 bg-surface border border-border rounded text-sm" data-id="element-6412">
              $2,000 - $3,500
            </div>
          </div>
          <div className="pt-2" data-id="element-6413">
            <div className="px-3 py-2 bg-primary/10 text-primary text-sm rounded font-medium text-center" data-id="element-6414">
              42 matching artists found
            </div>
          </div>
        </div>
      </div>
}, {
  icon: <Search className="w-5 h-5" data-id="element-6415" />,
  title: 'Compare artists side by side',
  description: 'See fit, availability, market relevance, and budget alignment before you reach out.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64 overflow-hidden" data-id="element-6416">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6417">
          Artist Comparison
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-6418">
          {[{
        name: 'The Wildwoods',
        genre: 'Folk',
        fee: '$2,500',
        fit: '96%',
        avail: 'Available'
      }, {
        name: 'Sarah Jarosz',
        genre: 'Americana',
        fee: '$4,200',
        fit: '91%',
        avail: 'Hold'
      }].map(artist => <div key={artist.name} className="bg-surface border border-border rounded-lg p-3" data-id="element-6419">
              <div className="text-sm font-semibold mb-2" data-id="element-6420">{artist.name}</div>
              <div className="space-y-1.5 text-xs" data-id="element-6421">
                <div className="flex justify-between" data-id="element-6422">
                  <span className="text-textMuted" data-id="element-6423">Genre</span>
                  <span data-id="element-6424">{artist.genre}</span>
                </div>
                <div className="flex justify-between" data-id="element-6425">
                  <span className="text-textMuted" data-id="element-6426">Fee</span>
                  <span className="font-medium" data-id="element-6427">{artist.fee}</span>
                </div>
                <div className="flex justify-between" data-id="element-6428">
                  <span className="text-textMuted" data-id="element-6429">Fit</span>
                  <span className="text-primary font-medium" data-id="element-6430">{artist.fit}</span>
                </div>
                <div className="pt-1" data-id="element-6431">
                  <div className={`px-2 py-1 ${artist.avail === 'Available' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'} rounded text-center`} data-id="element-6432">
                    {artist.avail}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
}, {
  icon: <Handshake className="w-5 h-5" data-id="element-6433" />,
  title: 'Keep the whole team aligned',
  description: 'Use role-based permissions, smart holds, deal workflows, and email-based approvals when speed matters.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6434">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6435">
          Deals Pipeline
        </div>
        <div className="grid grid-cols-3 gap-2" data-id="element-6436">
          {[{
        stage: 'Offer',
        count: 3,
        color: 'primary'
      }, {
        stage: 'Hold',
        count: 2,
        color: 'amber'
      }, {
        stage: 'Confirmed',
        count: 5,
        color: 'green'
      }].map(col => <div key={col.stage} data-id="element-6437">
              <div className="text-xs text-textMuted mb-2 flex items-center justify-between" data-id="element-6438">
                <span data-id="element-6439">{col.stage}</span>
                <span className="px-1.5 py-0.5 bg-surface rounded" data-id="element-6440">
                  {col.count}
                </span>
              </div>
              <div className="space-y-2" data-id="element-6441">
                {Array.from({
            length: Math.min(col.count, 2)
          }).map((_, i) => <div key={i} className={`bg-surface border border-${col.color}-500/30 rounded p-2`} data-id="element-6442">
                    <div className="text-xs font-medium mb-1" data-id="element-6443">
                      Artist {i + 1}
                    </div>
                    <div className="text-[10px] text-textMuted" data-id="element-6444">
                      Jun {12 + i}
                    </div>
                  </div>)}
              </div>
            </div>)}
        </div>
      </div>
}, {
  icon: <CalendarIcon className="w-5 h-5" data-id="element-6445" />,
  title: 'Your programming calendar, unified',
  description: 'See open dates, holds, confirmed shows, and advance tasks in one view.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6446">
        <div className="flex justify-between items-center mb-3" data-id="element-6447">
          <div className="text-sm font-semibold" data-id="element-6448">June 2026</div>
          <div className="flex gap-1" data-id="element-6449">
            <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/50" data-id="element-6450"></div>
            <div className="w-3 h-3 rounded bg-amber-500/20 border border-amber-500/50" data-id="element-6451"></div>
            <div className="w-3 h-3 rounded bg-border" data-id="element-6452"></div>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1" data-id="element-6453">
          {Array.from({
        length: 30
      }).map((_, i) => {
        const status = i % 7 === 5 ? 'confirmed' : i % 9 === 0 ? 'hold' : 'open';
        return <div key={i} className={`aspect-square rounded flex items-center justify-center text-xs ${status === 'confirmed' ? 'bg-green-500/10 border border-green-500/30 text-green-500' : status === 'hold' ? 'bg-amber-500/10 border border-amber-500/30 text-amber-500' : 'bg-surface border border-border text-textMuted'}`} data-id="element-6454">
                {i + 1}
              </div>;
      })}
        </div>
      </div>
}, {
  icon: <DollarSign className="w-5 h-5" data-id="element-6455" />,
  title: 'Close the loop on every booking',
  description: 'Generate contracts, track deposits, and monitor settlement without leaving the platform.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6456">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6457">
          Finance Overview
        </div>
        <div className="space-y-3" data-id="element-6458">
          <div className="grid grid-cols-3 gap-3" data-id="element-6459">
            <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6460">
              <div className="text-xs text-textMuted mb-1" data-id="element-6461">Expected</div>
              <div className="text-lg font-bold text-primary" data-id="element-6462">$24.5K</div>
            </div>
            <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6463">
              <div className="text-xs text-textMuted mb-1" data-id="element-6464">Deposits</div>
              <div className="text-lg font-bold text-green-500" data-id="element-6465">$8.2K</div>
            </div>
            <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6466">
              <div className="text-xs text-textMuted mb-1" data-id="element-6467">Outstanding</div>
              <div className="text-lg font-bold text-amber-500" data-id="element-6468">$6.1K</div>
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6469">
            <div className="flex justify-between items-center mb-2" data-id="element-6470">
              <div className="text-sm font-medium" data-id="element-6471">The Wildwoods</div>
              <div className="text-sm font-bold" data-id="element-6472">$2,500</div>
            </div>
            <div className="flex gap-2 text-xs" data-id="element-6473">
              <div className="px-2 py-1 bg-green-500/10 text-green-500 rounded" data-id="element-6474">
                Contract sent
              </div>
              <div className="px-2 py-1 bg-amber-500/10 text-amber-500 rounded" data-id="element-6475">
                Awaiting deposit
              </div>
            </div>
          </div>
        </div>
      </div>
}];
export function ForVenuesPage() {
  return <div data-id="element-6476">
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden" data-id="element-6477">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" data-id="element-6478"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-6479">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-5xl md:text-6xl font-bold tracking-tight mb-6 leading-tight" data-id="element-6480">
            Fill open dates with the right acts — faster.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg md:text-xl text-textMuted mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-6481">
            Create event needs, discover artists by fit and availability,
            compare options, send offers, manage contracts, and keep your
            programming calendar under control.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-6482">
            <button className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-textMain border border-border rounded-lg hover:bg-surface transition-colors" data-id="element-6483">
              Book a demo
              <ChevronRight className="w-4 h-4" data-id="element-6484" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      {features.map((feature, index) => <section key={index} className={`py-20 ${index % 2 === 0 ? 'bg-surface/10 border-y border-border' : ''}`} data-id="element-6485">
          <div className="max-w-6xl mx-auto px-6" data-id="element-6486">
            <div className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-6487">
              <motion.div initial={{
            opacity: 0,
            x: index % 2 === 0 ? -30 : 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className={index % 2 === 1 ? 'md:order-2' : ''} data-id="element-6488">
                <div className="w-12 h-12 rounded-lg bg-background border border-border flex items-center justify-center text-primary mb-6" data-id="element-6489">
                  {feature.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-6490">
                  {feature.title}
                </h2>
                <p className="text-lg text-textMuted leading-relaxed" data-id="element-6491">
                  {feature.description}
                </p>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            x: index % 2 === 0 ? 30 : -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className={index % 2 === 1 ? 'md:order-1' : ''} data-id="element-6492">
                {feature.mockup}
              </motion.div>
            </div>
          </div>
        </section>)}

      {/* Final CTA */}
      <section className="py-24 border-t border-border" data-id="element-6493">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-6494">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-6495">
            Ready to program smarter?
          </h2>
          <p className="text-lg text-textMuted mb-10" data-id="element-6496">
            Join venues who are filling their calendars with less guesswork.
          </p>
          <button className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-textMain border border-border rounded-lg hover:bg-surface transition-colors" data-id="element-6497">
            Book a demo
            <ChevronRight className="w-4 h-4" data-id="element-6498" />
          </button>
        </div>
      </section>
    </div>;
}